/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, IconButton, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import { ShowAlertState } from 'features/common/types';
import { PACKAGE_EXPLORE_QUOTE_API_BASE_URL } from '../../../../../../constants/constants';

interface ImageUploaderProps {
	onTstGenerated: (tst: string) => void;
	onSetAlert: (values: ShowAlertState) => void;
	disabled?: boolean;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ onTstGenerated, onSetAlert, disabled = false }) => {
	const [uploadedImages, setUploadedImages] = useState<File[]>([]);
	const [isProcessingImages, setIsProcessingImages] = useState(false);
	const cardRef = useRef<HTMLDivElement>(null);

	// Función para validar el tipo y tamaño de la imagen
	const validateImage = (file: File): boolean => {
		const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png'];

		if (!file.type.startsWith('image/') || !allowedFormats.includes(file.type)) {
			onSetAlert({
				show: true,
				severity: 'warning',
				message: `Formato no permitido: ${file.name}. Solo se permiten JPG, JPEG, PNG`,
			});
			return false;
		}

		const maxSizeBytes = 10 * 1024 * 1024; // 10MB
		if (file.size > maxSizeBytes) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: 'La imagen excede el límite de 10MB',
			});
			return false;
		}

		return true;
	};

	// Manejador de eventos paste
	const handlePaste = async (event: ClipboardEvent) => {
		if (disabled || isProcessingImages) return;

		const items = event.clipboardData?.items;
		if (!items) return;

		for (const item of Array.from(items)) {
			if (item.type.startsWith('image/')) {
				const file = item.getAsFile();
				if (file && validateImage(file)) {
					// Crear un nuevo File con un nombre más descriptivo
					const newFile = new File(
						[file],
						'clipboard_image_' + new Date().toISOString() + '.' + file.type.split('/')[1],
						{ type: file.type },
					);

					setUploadedImages((prev) => [...prev, newFile]);

					onSetAlert({
						show: true,
						severity: 'success',
						message: 'Imagen pegada exitosamente',
					});
				}
			}
		}
	};

	// Agregar y remover event listener
	useEffect(() => {
		const currentCard = cardRef.current;
		if (currentCard) {
			currentCard.addEventListener('paste', handlePaste);
		}

		return () => {
			if (currentCard) {
				currentCard.removeEventListener('paste', handlePaste);
			}
		};
	}, [disabled, isProcessingImages]);

	// Función para manejar la carga de imágenes
	const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files) {
			const newImages = Array.from(files).filter((file) => {
				if (!file.type.startsWith('image/')) return false;
				if (!validateImage(file)) return false;
				return true;
			});

			if (newImages.length > 0) {
				setUploadedImages((prev) => [...prev, ...newImages]);
			}
		}
		// Limpiar el input para permitir cargar la misma imagen nuevamente
		event.target.value = '';
	};

	// Función para eliminar una imagen cargada
	const handleRemoveImage = (index: number) => {
		setUploadedImages((prev) => prev.filter((_, i) => i !== index));
	};

	// Función para procesar imágenes con el endpoint local
	const processImagesWithLocalAPI = async () => {
		if (uploadedImages.length === 0) {
			onSetAlert({
				show: true,
				severity: 'warning',
				message: 'Por favor, carga al menos una imagen',
			});
			return;
		}

		// Validar límite de imágenes
		if (uploadedImages.length > 10) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: 'Máximo 10 imágenes permitidas',
			});
			return;
		}

		// Validar tamaño de imágenes (10MB por imagen)
		const maxSizeBytes = 10 * 1024 * 1024; // 10MB
		const oversizedImages = uploadedImages.filter((image) => image.size > maxSizeBytes);
		if (oversizedImages.length > 0) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: `${oversizedImages.length} imagen(es) exceden el límite de 10MB`,
			});
			return;
		}

		setIsProcessingImages(true);

		try {
			// Crear FormData para multipart/form-data
			const formData = new FormData();

			// Agregar todas las imágenes al FormData
			uploadedImages.forEach((image, index) => {
				console.log(`Agregando imagen ${index + 1}:`, {
					name: image.name,
					size: image.size,
					type: image.type,
				});
				formData.append('images[]', image);
			});

			// Log para debuggear
			console.log('FormData entries:');
			// @ts-ignore - FormData.entries() existe en browsers modernos
			for (const pair of formData.entries()) {
				console.log(pair[0], pair[1]);
			}

			// Llamada al endpoint local
			const response = await fetch(`${PACKAGE_EXPLORE_QUOTE_API_BASE_URL}vision/parse-itinerary`, {
				method: 'POST',
				body: formData,
				headers: {
					'X-BU': 'uy',
				},
				credentials: 'include',
				mode: 'cors',
			});

			console.log('Response status:', response.status);
			console.log('Response ok:', response.ok);

			// Log simplificado de headers
			const headerObj: any = {};
			response.headers.forEach((value, key) => {
				headerObj[key] = value;
			});
			console.log('Response headers:', headerObj);

			if (!response.ok) {
				const errorText = await response.text();
				console.error('Error response body:', errorText);

				// Intentar parsear como JSON si es posible
				try {
					const errorJson = JSON.parse(errorText);
					console.error('Error JSON:', errorJson);
					throw new Error(
						`Error del servidor: ${response.status} - ${errorJson.message || errorJson.error || errorText}`,
					);
				} catch (parseError) {
					throw new Error(`Error del servidor: ${response.status} - ${errorText}`);
				}
			}

			const data = await response.json();
			const formattedItinerary = data.content?.trim();

			if (formattedItinerary) {
				// Enviar el TST generado al componente padre
				onTstGenerated(formattedItinerary);

				// Limpiar las imágenes cargadas
				setUploadedImages([]);

				onSetAlert({
					show: true,
					severity: 'success',
					message: 'Imágenes procesadas exitosamente. TST poblado automáticamente.',
				});
			} else {
				throw new Error('No se pudo obtener el itinerario formateado del servidor');
			}
		} catch (error) {
			console.error('Error procesando imágenes:', error);
			onSetAlert({
				show: true,
				severity: 'error',
				message: `Error procesando imágenes: ${error instanceof Error ? error.message : 'Error desconocido'}`,
			});
		} finally {
			setIsProcessingImages(false);
		}
	};

	return (
		<Card
			ref={cardRef}
			sx={{
				alignSelf: 'anchor-center',
				border: '2px dashed #ccc',
				backgroundColor: '#fafafa',
				cursor: disabled || isProcessingImages ? 'not-allowed' : 'default',
				'&:focus': {
					outline: 'none',
					borderColor: 'primary.main',
				},
				transition: 'border-color 0.2s ease-in-out',
			}}
			tabIndex={disabled || isProcessingImages ? -1 : 0}
		>
			<CardContent>
				<Box display="flex" alignItems="center" gap={2} mb={2}>
					<input
						accept="image/*"
						style={{ display: 'none' }}
						id="image-upload-input"
						multiple
						type="file"
						onChange={handleImageUpload}
						disabled={disabled || isProcessingImages}
					/>
					<label htmlFor="image-upload-input">
						<Button
							variant="outlined"
							component="span"
							startIcon={<PhotoCameraIcon />}
							disabled={disabled || isProcessingImages}
						>
							Seleccionar Imágenes
						</Button>
					</label>

					{uploadedImages.length > 0 && (
						<Button
							variant="contained"
							color="success"
							onClick={processImagesWithLocalAPI}
							disabled={disabled || isProcessingImages}
							startIcon={isProcessingImages ? <CircularProgress size={20} /> : null}
						>
							{isProcessingImages
								? 'Procesando...'
								: `Procesar ${uploadedImages.length} imagen${uploadedImages.length > 1 ? 'es' : ''}`}
						</Button>
					)}
				</Box>

				<Typography variant="caption" color="textSecondary" display="block" mt={1} mb={2}>
					Carga una o más capturas de pantalla de itinerarios. Puedes seleccionar archivos o pegar imágenes directamente
					(Ctrl+V/Cmd+V). Las imágenes serán procesadas automáticamente y el TST será poblado con el formato Amadeus.
				</Typography>

				{/* Previsualización de imágenes cargadas */}
				{uploadedImages.length > 0 && (
					<Box>
						<Typography variant="subtitle2" gutterBottom>
							Imágenes cargadas ({uploadedImages.length}):
						</Typography>
						<Box display="flex" gap={1} flexWrap="wrap">
							{uploadedImages.map((image, index) => (
								<Box key={index} position="relative">
									<img
										src={URL.createObjectURL(image)}
										alt={`Captura ${index + 1}`}
										style={{
											width: 100,
											height: 100,
											objectFit: 'cover',
											borderRadius: 4,
											border: '1px solid #ddd',
										}}
									/>
									<IconButton
										size="small"
										onClick={() => handleRemoveImage(index)}
										disabled={disabled || isProcessingImages}
										sx={{
											position: 'absolute',
											top: -8,
											right: -8,
											backgroundColor: 'white',
											border: '1px solid #ddd',
											'&:hover': {
												backgroundColor: '#f5f5f5',
											},
										}}
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</Box>
							))}
						</Box>
					</Box>
				)}
			</CardContent>
		</Card>
	);
};
